import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { cn } from '@/shared/lib/utils';
import type { CalendarWindow } from '../model/calendarWindow';
import { buildMiniAxis, dayAtMiniAxisX, panMiniAxis } from '../model/miniAxis';

interface CalendarMiniAxisProps {
  /** Načtené okno měsíců — osa ho ukazuje včetně rezervy, do které se dá skočit. */
  window: CalendarWindow;
  monthNames: readonly string[];
  /** Šířka dne na ose v px. */
  dayWidth: number;
  /** Posuvná plocha kalendáře — osa si z ní sama čte polohu výřezu. */
  scroller: HTMLDivElement | null;
  /** Posuň osu tak, aby začínala daným (zlomkovým) dnem. */
  onScrollToDay: (day: number) => void;
}

/**
 * Zmenšená osa nad kalendářem (jen režim „Osa“): načtené okno s rezervou a v něm rámeček aktuálního
 * výřezu. Kliknutí i tažení posune kalendář tak, aby se cílové místo dostalo doprostřed výřezu.
 *
 * Měsíce mají minimální šířku, takže osa bývá širší než pruh — pak je uříznutá a posouvá se pod ním
 * (`panMiniAxis`), ale jen když se rámeček výřezu přiblíží ke kraji.
 *
 * Na posuv kalendáře se **odebírá sama** a drží si ho ve vlastním stavu. Kdyby ho držel workspace,
 * každý scroll event by překreslil celou plochu kalendáře (stovky pruhů a boxů) — a přitom se z něj
 * mění jediný obdélníček.
 */
export function CalendarMiniAxis({
  window: window_,
  monthNames,
  dayWidth,
  scroller,
  onScrollToDay,
}: CalendarMiniAxisProps) {
  const [viewport, setViewport] = useState({ left: 0, width: 0 });
  const [trackPx, setTrackPx] = useState(0);
  const [offset, setOffset] = useState(0);
  const trackRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!scroller) return;
    let frame = 0;
    const read = () => {
      frame = 0;
      setViewport((prev) =>
        prev.left === scroller.scrollLeft && prev.width === scroller.clientWidth
          ? prev
          : { left: scroller.scrollLeft, width: scroller.clientWidth }
      );
    };
    // Scroll umí přijít několikrát za snímek — čtení se sráží do jednoho rAF.
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(read);
    };
    read();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => read());
    observer?.observe(scroller);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      scroller.removeEventListener('scroll', onScroll);
      observer?.disconnect();
    };
  }, [scroller]);

  // Šířka pruhu určuje, jestli se osa vejde celá, nebo se bude posouvat.
  const setTrack = useCallback((element: HTMLDivElement | null) => {
    trackRef.current = element;
    if (element) setTrackPx(element.clientWidth);
  }, []);

  useEffect(() => {
    const element = trackRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => setTrackPx(element.clientWidth));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const model = useMemo(
    () =>
      buildMiniAxis({
        window: window_,
        monthNames,
        scrollLeft: viewport.left,
        viewportPx: viewport.width,
        dayWidth,
        trackPx,
      }),
    [window_, monthNames, viewport, dayWidth, trackPx]
  );

  const desiredOffset = panMiniAxis(model, trackPx, offset);
  useEffect(() => {
    if (desiredOffset !== offset) setOffset(desiredOffset);
  }, [desiredOffset, offset]);

  const handleMouseDown = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const rect = event.currentTarget.getBoundingClientRect();
      const visibleDays = (viewport.width || rect.width) / dayWidth;
      const jump = (clientX: number) => {
        const x = Math.min(rect.width, Math.max(0, clientX - rect.left));
        onScrollToDay(dayAtMiniAxisX(model, desiredOffset, x) - visibleDays / 2);
      };
      jump(event.clientX);
      const move = (native: globalThis.MouseEvent) => jump(native.clientX);
      const up = () => {
        document.removeEventListener('mousemove', move);
        document.removeEventListener('mouseup', up);
      };
      document.addEventListener('mousemove', move);
      document.addEventListener('mouseup', up);
    },
    [model, desiredOffset, viewport.width, dayWidth, onScrollToDay]
  );

  return (
    <div
      ref={setTrack}
      onMouseDown={handleMouseDown}
      data-testid="calendar-mini-axis"
      className="border-phx-border bg-phx-surface-2 relative h-[30px] shrink-0 cursor-pointer overflow-hidden border-b select-none"
    >
      <div
        className="absolute top-0 bottom-0 flex"
        style={{ left: -desiredOffset, width: model.contentWidth }}
      >
        {model.months.map((month) => (
          <div
            key={month.key}
            className={cn(
              'border-phx-border text-phx-2xs tracking-phx-wide box-border flex shrink-0 items-center justify-center overflow-hidden border-r font-semibold',
              month.inWindow
                ? 'bg-phx-surface text-phx-text-2'
                : 'text-phx-text-muted bg-transparent'
            )}
            style={{ width: month.width }}
          >
            <span className="max-w-full overflow-hidden px-0.5 text-ellipsis whitespace-nowrap">
              {month.label}
            </span>
          </div>
        ))}
        <div
          aria-hidden="true"
          className="phx-cal-mini-viewport rounded-phx-sm pointer-events-none absolute top-0.5 bottom-0.5 box-border min-w-[9px] border"
          style={{ left: model.viewportLeft, width: model.viewportWidth }}
        />
      </div>
    </div>
  );
}
